/* eslint-disable @typescript-eslint/no-empty-object-type */
import { CompanyStatusSegment } from "@/components/filter-groups/basic/CompanyStatusSegment";
import { CompanyTypeSegment } from "@/components/filter-groups/basic/CompanyTypeSegment";
import { TypeOfAuditorAssistanceSegment } from "@/components/filter-groups/economy/TypeOfAuditorAssistanceSegment";
import { CountrypartSegment } from "@/components/filter-groups/geography/CountrypartSegment";
import { MunicipalitiesSegment } from "@/components/filter-groups/geography/MunicipalitiesSegment";
import { PostalCodesSegment } from "@/components/filter-groups/geography/PostalCodesSegment";
import { RegionsSegment } from "@/components/filter-groups/geography/RegionsSegment";
import { BooleanFilter } from "@/components/filters/BooleanFilter";
import { CheckmarkFilter } from "@/components/filters/CheckmarkFilter";
import { DateFilter } from "@/components/filters/DateFilter";
import { DepartmentSizeFilter } from "@/components/filters/DepartmentSizeFilter";
import { EconomyFilter } from "@/components/filters/EconomyFilter";
import { EmployeesFilter } from "@/components/filters/EmployeesFilter";
import { EventFilter } from "@/components/filters/EventFilter";
import { IndustryFilter } from "@/components/filters/IndustryFilter";
import { LabelFilter } from "@/components/filters/LabelFilter";
import { NumberFilter } from "@/components/filters/NumberFilter";
import { StringFilter } from "@/components/filters/StringFilter";
import { TechnologyFilter } from "@/components/filters/TechnologyFilter";
import {
  Configuration,
  DepartmentSizeQueryFilter,
  EventQueryFilter,
  QueryFilter,
} from "@/types/campaign";
import { JSX } from "react";
import { FilterIdentifier } from "./config";

type DefaultFilterGroupProps<FilterId extends string> = {
  id: string;
  title: string;
  description?: string;
  keywords: string[];
  // FYI: Whether the filter is restricted for Essentials solutions.
  isAdvancedFilter?: boolean;
  filters: Record<FilterId, FilterIdentifier | null>;
  DisplayValue: (props: {
    filters: (QueryFilter | EventQueryFilter | DepartmentSizeQueryFilter)[];
    ignoreOperator?: boolean;
  }) => React.ReactNode;
  //FYI: Whether the filter group should take up the full width of the page
  fullWidth?: boolean;
};

type MatchCommonProps<Source, Target> = {
  [K in keyof Source]: K extends keyof Target
    ? Source[K] extends Target[K]
      ? Source[K]
      : Target[K]
    : Source[K];
};

export type FilterGroupType<FilterId extends string, Props extends {}> = {
  filters: readonly FilterId[];
  Component: (props: FilterGroupComponentProps<FilterId, Props>) => JSX.Element;
};

export type UpdateOptions = {
  skipNavigation?: boolean;
};

export type FilterGroupComponentProps<
  FilterId extends string,
  Props extends {},
> = {
  group: DefaultFilterGroupProps<FilterId> & Props;
  formId: string;
  queryFilters: Record<
    FilterId,
    QueryFilter | EventQueryFilter | DepartmentSizeQueryFilter | undefined
  >;
  update: (
    queryFilters: Record<
      FilterId,
      | Omit<QueryFilter, keyof FilterIdentifier>
      | Omit<EventQueryFilter, keyof FilterIdentifier>
      | Omit<DepartmentSizeQueryFilter, keyof FilterIdentifier>
      | undefined
    >,
    //FYI added because in industry filter we want to update the form values without navigating away from the page, but right now we dont need it in other filters, so we can make it optional
    options?: UpdateOptions,
  ) => Promise<void>;
  configuration?: Configuration;
  showButtons: boolean;
  setShowButtons: (show: boolean) => void;
  setIsDirty: (isDirty: boolean) => void;
  // getFilter: (targetFilter: {
  //   filterName: string;
  //   fieldName: string;
  // }) => QueryFilter | EventQueryFilter | undefined;
  // setFilter: (
  //   targetFilter: { filterName: string; fieldName: string },
  //   filter:
  //     | Omit<QueryFilter, "fieldName" | "filterName">
  //     | Omit<EventQueryFilter, "fieldName" | "filterName">
  //     | undefined,
  // ) => void;
};

function createFilterGroupType<FilterId extends string, Props extends {}>(
  options: FilterGroupType<FilterId, Props>,
) {
  return options;
}

export const filterGroupTypes = {
  boolean: createFilterGroupType({
    filters: ["value"],
    Component: BooleanFilter,
  }),
  checkmark: createFilterGroupType({
    filters: ["value"],
    Component: CheckmarkFilter,
  }),
  date: createFilterGroupType({
    filters: ["value"],
    Component: DateFilter,
  }),
  economy: createFilterGroupType({
    filters: ["value", "change"],
    Component: EconomyFilter,
  }),
  typeOfAuditorAssistance: createFilterGroupType({
    filters: ["value"],
    Component: TypeOfAuditorAssistanceSegment,
  }),
  // tagSelection: createFilterGroupType({
  //   filters: ["value"],
  //   Component: (props) => "Component goes here!",
  // }),
  technology: createFilterGroupType({
    filters: ["hasTechnology", "technology"],
    Component: TechnologyFilter,
  }),
  label: createFilterGroupType({
    filters: ["value"],
    Component: LabelFilter,
  }),
  event: createFilterGroupType({
    filters: ["value"],
    Component: EventFilter,
  }),
  string: createFilterGroupType({
    filters: ["value"],
    Component: StringFilter,
  }),
  number: createFilterGroupType({
    filters: ["value"],
    Component: NumberFilter,
  }),
  employees: createFilterGroupType({
    filters: ["value", "change"],
    Component: EmployeesFilter,
  }),
  departmentSize: createFilterGroupType({
    filters: ["value"],
    Component: DepartmentSizeFilter,
  }),
  industry: createFilterGroupType({
    filters: ["value"],
    Component: IndustryFilter,
  }),
  companyStatus: createFilterGroupType({
    filters: ["value"],
    Component: CompanyStatusSegment,
  }),
  companyType: createFilterGroupType({
    filters: ["value"],
    Component: CompanyTypeSegment,
  }),
  countryPart: createFilterGroupType({
    filters: ["value"],
    Component: CountrypartSegment,
  }),
  municipality: createFilterGroupType({
    filters: ["value"],
    Component: MunicipalitiesSegment,
  }),
  postalCode: createFilterGroupType({
    filters: ["value"],
    Component: PostalCodesSegment,
  }),
  region: createFilterGroupType({
    filters: ["value"],
    Component: RegionsSegment,
  }),
} as const;

export type FilterGroupTypes = typeof filterGroupTypes;

export type FilterGroup<FilterId extends string, Props extends {}> = {
  filterType: FilterGroupType<FilterId, Props>;
} & DefaultFilterGroupProps<FilterId> &
  Props;

export type FilterGroupFromType<T> =
  T extends FilterGroupType<infer FilterId, infer Props>
    ? FilterGroup<FilterId, Props>
    : never;

export type TypedFilterGroup = {
  [K in keyof FilterGroupTypes]: FilterGroupFromType<FilterGroupTypes[K]>;
}[keyof FilterGroupTypes];

export type AnyFilterGroup = FilterGroup<string, {}>;

export function createFilterGroup<FilterId extends string, Props extends {}>(
  filterGroup: FilterGroup<FilterId, Props>,
) {
  return filterGroup;
}
